import React, { useState } from 'react';
import { Grid } from '@mui/material';
import { useParams } from 'react-router-dom';
import {
  QualificationCheckForm,
  QUALIFICATION_CHECK_SUBMIT_TYPE,
} from './QualificationCheckForm';
import type { UpdateQualificationCheckInput } from './types';
import {
  useGetQualificationCheck,
  useUpdateARQualificationCheck,
  useSubmitARQualificationCheck,
} from '../../graphql';
import { QUALIFICATION_PAGE_TEXT } from '../../lang';
import {
  Accordion,
  Alert,
  type AlertDetails,
  Loading,
} from '../../../../components';

export type ARQualificationDetailsProps = {
  applicationId?: string;
};

export const ARQualificationDetails: React.FunctionComponent<
  ARQualificationDetailsProps
> = props => {
  const { applicationId = '' } = useParams();
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  const {
    qualificationCheck,
    application,
    applicantBasic,
    loading,
    error: qualificationCheckError,
    refetch: refetchQC,
  } = useGetQualificationCheck(applicationId);

  const updateARQualificationCheck = useUpdateARQualificationCheck();
  const submitARQualificationCheck = useSubmitARQualificationCheck();
  const handleSubmit = async (
    submitType: QUALIFICATION_CHECK_SUBMIT_TYPE,
    value: Omit<UpdateQualificationCheckInput, 'applicationId'>,
  ): Promise<void> => {
    const { remark = '', ...others } = value;
    try {
      const variables = {
        input: {
          applicationId,
          remark,
          ...others,
        },
      };
      switch (submitType) {
        case QUALIFICATION_CHECK_SUBMIT_TYPE.UPDATE:
          await updateARQualificationCheck({
            variables,
          });
          setAlertDetails({
            alertContent:
              QUALIFICATION_PAGE_TEXT.ALERT_MESSAGE.SAVED_SUCCESSFULLY,
            alertType: 'success',
          });
          setOpenAlert(true);
          refetchQC();
          break;
        case QUALIFICATION_CHECK_SUBMIT_TYPE.RELEASE:
          await submitARQualificationCheck({
            variables,
          });
          setAlertDetails({
            alertContent:
              QUALIFICATION_PAGE_TEXT.ALERT_MESSAGE.RELEASED_SUCCESSFULLY,
            alertType: 'success',
          });
          setOpenAlert(true);
          refetchQC();
          break;
        default:
          break;
      }
    } catch (error: any) {
      setAlertDetails({
        alertContent: `${QUALIFICATION_PAGE_TEXT.ALERT_MESSAGE.SAVE_FAILED}${(error as Error)?.message}`,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  if (qualificationCheckError instanceof Error) {
    return (
      // resolve too-many rander problem
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.${qualificationCheckError.message}`,
          alertType: 'error',
        }}
      />
    );
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Grid
        container
        direction="column"
        sx={{
          marginBottom: '1rem',
        }}
      >
        <Accordion
          title={QUALIFICATION_PAGE_TEXT.TITLE}
          defaultExpanded
          details={
            <QualificationCheckForm
              applicantBasic={applicantBasic}
              application={application}
              qualificationCheck={qualificationCheck}
              onSubmit={handleSubmit}
            />
          }
        />
      </Grid>
    </>
  );
};
